import React from 'react';
import { OptImg } from '../components/ui/OptImg';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { SEGMENTS } from './segmentContent';
import { Breadcrumbs } from '../components/website/Breadcrumbs';
import { ProductCmsContent } from '../components/website/ProductCmsContent';
import { ProductNextSteps } from '../components/website/ProductNextSteps';

// Segments with editable guide content in the page builder.
const CMS_SEGMENTS = ['residential', 'commercial', 'architectural', 'motorized-skylights', 'smart-home-integration', 'motorized-blinds'];

const QuoteBtn = ({ id }) => (
  <Link to="/quote" className="inline-flex w-full sm:w-fit justify-center items-center gap-2 border border-amber-500 hover:border-amber-600 bg-amber-500 hover:bg-amber-600 text-white px-7 py-3.5 text-sm font-medium uppercase tracking-[0.2em] transition-colors" data-testid={id}>
    Request a Quote <ArrowRight className="w-4 h-4" />
  </Link>
);

// Project-led segments also offer a consultation as an outlined secondary action.
const CONSULT_SEGMENTS = ['architectural', 'commercial'];
const ConsultBtn = ({ id }) => (
  <Link to="/consultation" className="inline-flex w-full sm:w-fit justify-center items-center gap-2 border border-white/70 hover:bg-white hover:text-neutral-900 text-white px-7 py-3.5 text-sm font-medium uppercase tracking-[0.2em] transition-colors" data-cta="Book a Consultation" data-testid={id}>
    Book a Consultation
  </Link>
);
const Actions = ({ segment, place }) => (
  <div className="flex flex-col sm:flex-row gap-3 max-w-xs sm:max-w-none">
    <QuoteBtn id={`segment-${place}-quote-btn`} />
    {CONSULT_SEGMENTS.includes(segment) && <ConsultBtn id={`segment-${place}-consult-btn`} />}
  </div>
);

const Hero = ({ s, segment }) => (
  <section className="relative h-[78vh] min-h-[520px] overflow-hidden" data-testid="segment-hero">
    <OptImg priority src={s.hero} alt={s.heroAlt} className="absolute inset-0 h-full w-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/40 to-transparent" />
    <div className="relative container-xl h-full flex flex-col justify-end pb-16 md:pb-24 text-white">
      <Breadcrumbs light className="mb-6" />
      <span className="label-overline text-amber-400">{s.eyebrow}</span>
      <h1 className="mt-3 font-serif font-light !text-5xl md:!text-7xl tracking-tight" data-testid="segment-hero-title">{s.title}</h1>
      <p className="mt-5 max-w-xl text-base md:text-lg text-white/80 leading-relaxed">{s.tagline}</p>
      <div className="mt-8"><Actions segment={segment} place="hero" /></div>
    </div>
  </section>
);

const Intro = ({ s }) => (
  <section className="container-xl py-20 md:py-28 grid md:grid-cols-12 gap-10 md:gap-16 items-center" data-testid="segment-intro">
    <div className="md:col-span-6 order-2 md:order-1">
      <span className="label-overline text-amber-600">Overview</span>
      <h2 className="mt-3 font-serif !text-3xl md:!text-5xl font-light leading-tight text-neutral-900">{s.introTitle}</h2>
      <div className="mt-6 space-y-5 text-neutral-600 leading-relaxed">{s.intro.map((p) => <p key={p.slice(0, 20)}>{p}</p>)}</div>
    </div>
    <div className="md:col-span-6 order-1 md:order-2 aspect-[3/2] overflow-hidden bg-neutral-200">
      <OptImg sizes="(min-width: 768px) 50vw, 100vw" src={s.secondary} alt={s.secondaryAlt} loading="lazy" className="h-full w-full object-cover" data-testid="segment-intro-img" />
    </div>
  </section>
);

const IconGrid = ({ id, eyebrow, title, items, dark }) => (
  <section className={dark ? 'bg-neutral-950 text-white' : 'bg-[#f6f2eb]'} data-testid={`segment-${id}`}>
    <div className="container-xl py-20 md:py-28">
      <span className={`label-overline ${dark ? 'text-amber-400' : 'text-amber-600'}`}>{eyebrow}</span>
      <h2 className="mt-3 font-serif !text-3xl md:!text-5xl font-light max-w-2xl">{title}</h2>
      <div className="mt-12 md:mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-10">
        {items.map(({ icon: Icon, title: t, text }) => (
          <div key={t} className={`border-t pt-6 ${dark ? 'border-white/15' : 'border-neutral-300'}`}>
            <Icon className={`w-6 h-6 ${dark ? 'text-amber-400' : 'text-amber-700'}`} strokeWidth={1.5} />
            <h3 className="mt-5 font-serif !text-xl md:!text-2xl font-light">{t}</h3>
            <p className={`mt-2 text-sm leading-relaxed ${dark ? 'text-white/65' : 'text-neutral-600'}`}>{text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const RelatedProducts = ({ s }) => (
  <section className="container-xl py-20 md:py-28" data-testid="segment-products">
    <span className="label-overline text-amber-600">Relevant Products</span>
    <h2 className="mt-3 font-serif !text-3xl md:!text-5xl font-light text-neutral-900">Explore the collection</h2>
    <div className="mt-10 border-t border-neutral-300">
      {s.products.map((p) => (
        <Link key={p.to} to={p.to} className="group flex items-center justify-between gap-6 border-b border-neutral-300 py-6 transition-colors hover:bg-white/70 px-1" data-testid={`segment-product-link-${p.to.split('/').pop()}`}>
          <div>
            <h3 className="font-serif !text-2xl md:!text-3xl font-light text-neutral-900">{p.name}</h3>
            <p className="mt-1 text-sm text-neutral-600">{p.text}</p>
          </div>
          <ArrowUpRight className="w-5 h-5 shrink-0 text-neutral-400 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-amber-600" />
        </Link>
      ))}
    </div>
  </section>
);

const Cta = ({ s, segment }) => (
  <section className="relative overflow-hidden bg-neutral-900 text-white" data-testid="segment-cta">
    <OptImg loading="lazy" src={s.hero} alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
    <div className="relative container-xl py-20 md:py-28 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
      <div>
        <h2 className="font-serif !text-3xl md:!text-5xl font-light max-w-xl">{s.ctaTitle}</h2>
        <p className="mt-4 text-white/70 max-w-lg">{s.ctaText}</p>
      </div>
      <Actions segment={segment} place="cta" />
    </div>
  </section>
);

export const SegmentPage = ({ segment }) => {
  const s = SEGMENTS[segment];
  return (
    <Layout>
      <div className="bg-neutral-50" data-testid={`segment-page-${segment}`}>
        <Hero s={s} segment={segment} />
        <Intro s={s} />
        <IconGrid id="benefits" eyebrow="Benefits" title={s.benefitsTitle} items={s.benefits} dark />
        <IconGrid id="applications" eyebrow="Typical Applications" title={s.applicationsTitle} items={s.applications} />
        {CMS_SEGMENTS.includes(segment) && <ProductCmsContent slug={segment} />}
        <RelatedProducts s={s} />
        <ProductNextSteps slug={segment} name={s.title.toLowerCase()} supportOnly />
        <Cta s={s} segment={segment} />
      </div>
    </Layout>
  );
};
